import { Checkbox } from '@mantine/core';
import { IconRocket } from '@tabler/icons-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, NewTabLink } from '../../../../../components/Atoms';
import type { ContainerConfigFormValue } from './ContainerConfigForm';
import PricingSummary from './PricingSummary';

type PricingType = 'request_based' | 'instance_based';
type SourceType = 'url' | 'inline' | 'github';

interface ContainerDeploymentSummaryCardProps {
    name: string;
    pricingType: PricingType;
    sourceType: SourceType;
    config: ContainerConfigFormValue;
    onDeploy: () => void;
    isDeploying: boolean;
    isValid: boolean;
    /** Terms + Deploy only belong on the final review step; earlier steps get a hint instead. */
    showDeployAction: boolean;
    /** Rendered above the deploy action, e.g. `DeployTargetNotice`. */
    notice?: ReactNode;
    /** Set when the caller may not deploy here: the button stays focusable, described by this id. */
    deployBlockedReasonId?: string;
}

function pricingTypeKey(p: PricingType): 'requestBased' | 'instanceBased' {
    return p === 'request_based' ? 'requestBased' : 'instanceBased';
}

function ConfigRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex flex-row justify-between items-center text-sm">
            <span className="text-fg/68">{label}</span>
            <span className="font-mono text-fg">{value}</span>
        </div>
    );
}

export default function ContainerDeploymentSummaryCard({
    name,
    pricingType,
    sourceType,
    config,
    onDeploy,
    isDeploying,
    isValid,
    showDeployAction,
    notice,
    deployBlockedReasonId,
}: ContainerDeploymentSummaryCardProps) {
    const { t } = useTranslation('containerEngine');
    const [accepted, setAccepted] = useState(false);

    return (
        <div className="sticky top-4 self-start w-full">
            <div className="w-full rounded-md bg-surface-1 border-[0.5px] border-line overflow-hidden">
                <div
                    style={{
                        background: 'var(--summary-hero-gradient)',
                    }}
                    className="w-full p-4 flex flex-col justify-start items-start gap-3 border-b-[0.5px] border-line"
                >
                    <div className="inline-flex text-xs items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <span className="uppercase text-fg/80">
                            {t('deployment.summaryCard.readyToDeploy', 'Ready to deploy')}
                        </span>
                    </div>
                    <div className="flex flex-col justify-start items-start gap-1">
                        <span className="text-lg font-mono font-bold text-fg truncate">
                            {name || t('deployment.summaryCard.noName', 'unnamed-service')}
                        </span>
                        <div className="inline-flex items-center text-sm text-fg/68 gap-1">
                            <span>
                                {t(
                                    `deployment.pricing.${pricingTypeKey(pricingType)}`,
                                    pricingType === 'request_based'
                                        ? 'Request-based'
                                        : 'Instance-based'
                                )}
                            </span>
                            <span className="text-xl leading-0">·</span>
                            <span>
                                {t(
                                    `deployment.sourceTypes.${sourceType}`,
                                    sourceType === 'url'
                                        ? 'Container image'
                                        : sourceType === 'inline'
                                          ? 'Inline Dockerfile'
                                          : 'GitHub repository'
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="w-full p-4 grid grid-cols-1 gap-2 border-b-[0.5px] border-line">
                    <ConfigRow
                        label={t('deployment.summaryCard.configCpu', 'CPU')}
                        value={`${config.cpu}m`}
                    />
                    <ConfigRow
                        label={t('deployment.summaryCard.configMemory', 'Memory')}
                        value={`${config.memory} MB`}
                    />
                    <ConfigRow
                        label={t('deployment.summaryCard.configReplicas', 'Replicas')}
                        value={t('deployment.summaryCard.configReplicasValue', '{{min}}–{{max}}', {
                            min: config.min_replicas,
                            max: config.max_replicas,
                        })}
                    />
                </div>

                <div className="w-full p-4 border-b-[0.5px] border-line">
                    <PricingSummary config={config} pricingType={pricingType} compact />
                </div>

                <div className="w-full p-4 flex flex-col gap-3.5">
                    {notice}
                    {!showDeployAction && (
                        <p className="text-[13px] text-fg/68">
                            {t(
                                'deployment.summaryCard.deployHint',
                                'Finish the remaining steps to deploy.'
                            )}
                        </p>
                    )}
                    {showDeployAction && (
                        <>
                            <Checkbox
                                size="xs"
                                checked={accepted}
                                onChange={(e) => setAccepted(e.currentTarget.checked)}
                                color="blue"
                                variant="filled"
                                label={
                                    <span className="text-[13px] text-fg">
                                        {t('deployment.summaryCard.termsPrefix', 'I agree to the')}{' '}
                                        <NewTabLink
                                            href="https://xver.cloud/th/legal/terms-of-service"
                                            className="text-blue-400 light:text-blue-700 hover:underline"
                                        >
                                            {t(
                                                'deployment.summaryCard.termsLink',
                                                'terms of service'
                                            )}
                                        </NewTabLink>
                                    </span>
                                }
                            />
                            <Button
                                width={'100%'}
                                leftIcon={<IconRocket size={16} />}
                                disabled={!accepted || !isValid || isDeploying}
                                isLoading={isDeploying}
                                aria-disabled={deployBlockedReasonId ? true : undefined}
                                aria-describedby={deployBlockedReasonId}
                                style={
                                    deployBlockedReasonId
                                        ? { opacity: 0.5, cursor: 'not-allowed' }
                                        : undefined
                                }
                                onClick={deployBlockedReasonId ? undefined : onDeploy}
                            >
                                {t('deployment.actions.deploy', 'Deploy container')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
